<template>
  <div class="container member-qr-page">
    <o-bread-crumb-mobile>
      <template #pageDetail>QR Code</template>
      <template #pageDescription>
        Show this QR Code to your merchant to process <br />
        your transaction.
      </template>
    </o-bread-crumb-mobile>

    <div class="page-wrapper">
      <!-- LEFT : QR CODE -->
      <div class="card">
        <div class="card-header">
          <h3 class="card-header-title">Member QR Code</h3>
        </div>

        <div class="card-body">
          <div class="member-info">
            <div class="member-avatar-wrapper">
              <img
                v-if="memberAvatar"
                :src="memberAvatar"
                class="member-avatar"
              />
              <div v-else class="member-avatar-placeholder">
                {{ getInitials }}
              </div>
            </div>

            <h3 class="member-name">{{ displayMemberName }}</h3>
            <p class="member-club">{{ corporateName }} Member</p>

            <div class="member-tier">
              <i class="fas fa-medal"></i>
              <span>{{ displayTierName }}</span>
            </div>
          </div>

          <div class="qr-code-wrapper">
            <canvas ref="qrCanvas" class="qrcode"></canvas>
          </div>
        </div>
      </div>

      <!-- RIGHT : MEMBER DETAILS -->
      <div class="card">
        <div class="card-header">
          <h3 class="card-header-title">Member Details</h3>
        </div>

        <div class="card-body">
          <div class="member-stats" :class="{ 'single-grid': !isPointSystemActive }">
            <div class="stat-item">
              <div class="stat-icon">
                <img :src="displayTierIcon" />
              </div>
              <div class="stat-content">
                <span class="stat-label">Tier</span>
                <span class="stat-value">{{ displayTierName }}</span>
              </div>
            </div>

            <div class="stat-item" v-if="isPointSystemActive">
              <div class="stat-icon">
                <img src="/img/member-icon/points-logo.svg" />
              </div>
              <div class="stat-content">
                <span class="stat-label">Points</span>
                <span class="stat-value">{{ displayPoints }}</span>
              </div>
            </div>

            <div class="stat-item" v-if="isPointSystemActive">
              <div class="stat-icon">
                <img src="/img/member-icon/voucher-logo.svg" />
              </div>
              <div class="stat-content">
                <span class="stat-label">Vouchers</span>
                <span class="stat-value">{{ displayVouchers }}</span>
              </div>
            </div>
          </div>

          <div class="member-details">
            <div class="detail-item">
              <span class="detail-label">Member ID</span>
              <span class="detail-value">{{ memberId }}</span>
            </div>

            <div class="detail-item">
              <span class="detail-label">Full Name</span>
              <span class="detail-value">{{ displayMemberName }}</span>
            </div>

            <div class="detail-item" v-if="displayEmail">
              <span class="detail-label">Email</span>
              <span class="detail-value">{{ displayEmail }}</span>
            </div>

            <div class="detail-item" v-if="displayPhone">
              <span class="detail-label">Phone</span>
              <span class="detail-value">{{ displayPhone }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcode'
import { ref, computed, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import { usePointSystem } from '@/composables/pointsystem'
import { OBreadCrumbMobile } from '@/components/OmnihControl'

export default {
  name: 'MemberQrCode',
  components: { OBreadCrumbMobile },
  setup() {
    const store = useStore()
    const { isPointSystemActive } = usePointSystem()
    const qrCanvas = ref(null)
    
    // Ambil userInfo langsung dari store
    const userInfo = computed(() => store.state.Auth.userInfo)

    // Computed untuk data member - prioritas dari store userInfo
    const memberId = computed(() => userInfo.value?.member?.alias_id || userInfo.value?.member?.id || '-')
    const corporateName = computed(() => userInfo.value?.member?.corporate_name || '')
    const memberAvatar = computed(() => userInfo.value?.member?.member_avatar || '')

    const displayPoints = computed(() => {
      const points = userInfo.value?.member?.points ?? 0
      return points
    })

    const displayVouchers = computed(() => {
      return store.state.ClientDashboard?.dashboard?.owned_vouchers ?? userInfo.value?.member?.filtered_vouchers?.length ?? 0
    })

    const displayEmail = computed(() => {
      const email = userInfo.value?.email || ''
      return email
    })

    const displayPhone = computed(() => {
      const phone = userInfo.value?.user_profile?.phone || ''
      // Return '-' for null, empty, or default phone number
      if (!phone || phone === '+6281234567890' || phone === '6281234567890') {
        return '-'
      }
      return phone
    })

    const displayTierName = computed(() => {
      return userInfo.value?.member?.tier_name || '-'
    })

    const displayTierIcon = computed(() => {
      const storeVal = userInfo.value?.member?.tier?.icon?.image_url
      const dashboardVal = store.state.ClientDashboard?.dashboard?.tier?.icon?.image_url
      const storeVal2 = userInfo.value?.member?.tier?.image_url
      const icon = storeVal || dashboardVal || storeVal2 || '/img/member-icon/tier-logo.svg'
      return icon
    })

    const displayMemberName = computed(() => {
      const u = userInfo.value
      if (u && (u.first_name || u.last_name)) {
        return `${u.title || ''} ${u.first_name || ''} ${u.last_name || ''}`.trim()
      }
      return '-'
    })

    const getInitials = computed(() => {
      const name = displayMemberName.value
      if (!name) return '?'
      const words = name.split(' ').filter(Boolean)
      if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    })

    const loadQrCode = () => {
      if (!qrCanvas.value) return
      const qrId = userInfo.value?.member?.id || userInfo.value?.member?.alias_id || '-'
      const qrobj = { "memberId": qrId }
      const qrstring = JSON.stringify(qrobj)

      const options = {
        width: 220,
        margin: 2,
        scale: 10,
        color: {
          dark: '#1B1E28',
          light: '#FFFFFF'
        }
      }

      QRCode.toCanvas(qrCanvas.value, qrstring, options, function (error) {
        if (error) console.error(error)
      })
    }

    watch(() => userInfo.value, (newVal) => {
      if (newVal?.member) {
        nextTick(() => {
          loadQrCode()
        })
      }
    }, { immediate: true })

    return {
      qrCanvas,
      memberId,
      corporateName,
      memberAvatar,
      displayPoints,
      displayVouchers,
      displayEmail,
      displayPhone,
      displayTierName,
      displayTierIcon,
      displayMemberName,
      getInitials,
      isPointSystemActive
    }
  }
}
</script>

<style scoped>
.member-qr-page {
  padding-bottom: 40px;
}

.page-wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  max-width: 960px;
  margin: 0 auto;
}

.card {
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
}

.card-header {
  background: var(--primary-gradient, linear-gradient(135deg, #F93E3E 0%, #E02020 100%));
  padding: 20px 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.card-header-title {
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
}

.card-body {
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
}

.member-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.member-avatar-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  border: 4px solid var(--primary-border, #F93E3E);
  box-shadow: 0 8px 24px rgba(249, 62, 62, 0.2);
}

.member-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.member-avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--primary-gradient, linear-gradient(135deg, #F93E3E 0%, #E02020 100%));
  color: #ffffff;
  font-size: 28px;
  font-weight: 600;
}

.member-name {
  font-size: 20px;
  font-weight: 600;
  color: #1B1E28;
  margin: 0;
  text-align: center;
}

.member-club {
  font-size: 13px;
  color: #717784;
  margin: 0;
}

.member-tier {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: var(--secondary-color, rgba(249, 62, 62, 0.08));
  border-radius: 20px;
  color: var(--primary-color, #F93E3E);
  font-size: 13px;
  font-weight: 600;
}

.qr-code-wrapper {
  background: #ffffff;
  padding: 20px;
  border-radius: 16px;
  border: 2px solid #E1E4EA;
  display: flex;
  justify-content: center;
  align-items: center;
}

.qrcode {
  display: block;
  border-radius: 8px;
}

.member-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  width: 100%;
  margin-bottom: 8px;
}

.member-stats.single-grid {
  grid-template-columns: 1fr;
}

.stat-item {
  background: #F5F6FA;
  border-radius: 12px;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.stat-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.stat-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.stat-label {
  font-size: 11px;
  color: #717784;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.stat-value {
  font-size: 16px;
  font-weight: 600;
  color: #1B1E28;
}

.member-details {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 16px;
  border-bottom: 1px solid #E1E4EA;
}

.detail-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.detail-label {
  font-size: 12px;
  color: #717784;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.detail-value {
  font-size: 15px;
  font-weight: 500;
  color: #1B1E28;
  word-break: break-word;
}

@media (max-width: 768px) {
  .page-wrapper {
    grid-template-columns: 1fr;
  }

  .card-body {
    padding: 24px 20px;
  }

  .member-avatar-wrapper {
    width: 70px;
    height: 70px;
  }

  .member-name {
    font-size: 18px;
  }

  .stat-value {
    font-size: 14px;
  }
}
</style>
